import {
  Application,
  FederatedPointerEvent,
  FederatedWheelEvent,
} from 'pixi.js';
import { LottieSprite } from './LottieSprite.ts';
import {
  AnimationCharacter,
  AssetType,
  CharacterRef,
  Position,
} from '../../../types/typing.ts';
import { startRecording } from './recordAnimation.ts';
import { drawBorder } from './drawBorder.ts';
import { stopRecording } from './recordAnimation.ts';

export const setupCharacter = async (
  charData: AnimationCharacter,
  app: Application | null,
  charactersRef: React.MutableRefObject<Map<string, CharacterRef>>,
  onUpdateCharacter: (
    id: string,
    properties: { position?: Position; scale?: number; pathPoints?: Position[] }
  ) => void,
  isRecordingRef: React.MutableRefObject<boolean | null | undefined>,
  recordingStartTimeRef: React.MutableRefObject<number | null | undefined>,
  onAssetSelection: (id: string, type: AssetType) => void,
  selectedMenuRef: React.MutableRefObject<string | null | undefined>,
  setLastSelectedCharacterIndex: (id: string) => void
) => {
  try {
    if (!app) return;

    // Create Lottie animation instance
    const lottieAnimation = new LottieSprite({
      asset: charData.animation,
      autoplay: true,
      loop: true,
      width: 100,
      height: 100,
      speed: 1,
    });

    // Initial position and scale
    const initialPosition = {
      x: charData.position.x,
      y: charData.position.y,
    };
    const initialScale = charData.scale;

    onUpdateCharacter(charData.id, {
      position: initialPosition,
      scale: initialScale,
    });

    // Configure sprite properties
    lottieAnimation.eventMode = 'static';
    lottieAnimation.cursor = 'pointer';
    lottieAnimation.zIndex = 1;
    lottieAnimation.position.set(initialPosition.x, initialPosition.y);
    lottieAnimation.scale.set(initialScale);

    app.stage.addChild(lottieAnimation);

    const characterRefObject: CharacterRef = {
      id: charData.id,
      animation: charData.animation,
      sprite: lottieAnimation,
      position: initialPosition,
      pathCircles: [],
      isDragging: false,
      scale: initialScale,
      isSelected: false,
      recordedMovements: [],
      timeline: null,
      isRecording: false,
      pathPoints: charData.pathPoints || [],
      borderGraphics: null,
      destroy: () => {
        console.log(`Cleaning up character ${charData.id}`);
        console.log('Lottie Animation exists:', lottieAnimation);
        lottieAnimation
          .off('pointerdown', onPointerDown)
          .off('pointermove', onPointerMove)
          .off('pointerup', onPointerUp)
          .off('pointerupoutside', onPointerOutside)
          .off('wheel', onWheel);

        if (lottieAnimation) {
          if (characterRefObject.borderGraphics) {
            characterRefObject.sprite.removeChild(
              characterRefObject.borderGraphics
            );
          }
          app.stage.removeChild(lottieAnimation);
          lottieAnimation.destroy();
        } else {
          console.log('Lottie Animation is null or undefined');
        }
        if (characterRefObject.timeline) {
          characterRefObject.timeline.kill();
        }
      },
    };

    charactersRef.current.set(charData.id, characterRefObject);
    //updateCharacterVisuals(characterRef);

    // Interaction state
    let isDragging = false;
    let dragOffset: Position = { x: 0, y: 0 };
    lottieAnimation.interactive = true;

    const selectCharacter = () => {
      charactersRef.current.forEach((ref) => {
        ref.isSelected = ref === characterRefObject;
        if (ref.isSelected) {
          onAssetSelection(ref.id, AssetType.CHARACTER);
          renderBorder(ref); // Draw border for selected character
        } else {
          removeBorder(ref); // Remove border from others
        }
        ref.sprite.on('scale', () => {
          if (ref.borderGraphics) {
            ref.borderGraphics.scale.set(
              ref.sprite.scale.x,
              ref.sprite.scale.y
            );
          }
        });
        //updateCharacterVisuals(ref);
      });
    };

    const renderBorder = (characterRef: CharacterRef) => {
      // First, remove any existing border
      if (characterRef.borderGraphics) {
        //characterRef.sprite.removeChild(characterRef.borderGraphics);
        characterRef.borderGraphics.visible = true;
        return;
      }
      console.log('Drawing border');

      // Create new border graphics
      const borderGraphics = drawBorder(10, 10, 180, 180, 0x36cedc);
      characterRef.borderGraphics = borderGraphics;
      characterRef.sprite.addChild(borderGraphics);
    };

    const removeBorder = (characterRef: CharacterRef) => {
      // Remove border if it exists
      if (characterRef.borderGraphics) {
        //characterRef.sprite.removeChild(characterRef.borderGraphics);
        characterRef.borderGraphics.visible = false;
      }
    };

    // PIXI Stage Click Handler (Click inside Pixi Canvas)
    app.stage.on('pointerdown', (event) => {
      console.log('Clicked inside Pixi Canvas');

      let clickedOnSprite = false;
      charactersRef.current.forEach((char) => {
        if (char?.sprite.containsPoint(event.data.global)) {
          char.isSelected = true;
          clickedOnSprite = true;
        }
      });

      // If NOT clicking on a sprite, remove selection
      if (!clickedOnSprite) {
        charactersRef.current.forEach((char) => {
          if (char.isSelected) {
            char.isSelected = false;
            if (char.borderGraphics) char.borderGraphics.visible = false;
            onAssetSelection('', AssetType.NONE);
          }
        });
      }
    });

    // Pointer down handler
    const onPointerDown = (event: FederatedPointerEvent) => {
      event.stopPropagation();
      selectCharacter();
      isDragging = true;
      characterRefObject.isDragging = true;
      const globalPosition = event.data.global;
      const spriteGlobalPosition = lottieAnimation.getGlobalPosition();
      dragOffset = {
        x: globalPosition.x - spriteGlobalPosition.x,
        y: globalPosition.y - spriteGlobalPosition.y,
      };
      lottieAnimation.alpha = 0.8;
    };

    const onPointerMove = (event: FederatedPointerEvent) => {
      event.stopPropagation();
      if (
        isDragging &&
        characterRefObject.isDragging &&
        lottieAnimation.parent
      ) {
        const newPosition = event.data.getLocalPosition(lottieAnimation.parent);
        lottieAnimation.x = newPosition.x - dragOffset.x;
        lottieAnimation.y = newPosition.y - dragOffset.y;
        characterRefObject.position.x = lottieAnimation.x;
        characterRefObject.position.y = lottieAnimation.y;

        if (
          isRecordingRef.current &&
          recordingStartTimeRef.current !== null &&
          selectedMenuRef.current === 'animations'
        ) {
          startRecording(event, charactersRef, charData.id, app, dragOffset);
        } else {
          onUpdateCharacter(charData.id, {
            position: {
              x: lottieAnimation.x,
              y: lottieAnimation.y,
            },
          });
        }
      }
    };

    // Pointer up handler
    const onPointerUp = () => {
      if (!isDragging) return;
      console.log('On pointer up clicked');
      setLastSelectedCharacterIndex(characterRefObject.id);
      isDragging = false;
      characterRefObject.isDragging = false;
      dragOffset = { x: 0, y: 0 };
      lottieAnimation.alpha = 1;

      // Stop recording if we were recording
      if (isRecordingRef.current && recordingStartTimeRef.current !== null) {
        const pathPoints = stopRecording(charactersRef, onUpdateCharacter);
        onUpdateCharacter(charData.id, { pathPoints: pathPoints });
      }
    };

    const onPointerOutside = () => {
      if (!isDragging) return;
      isDragging = false;
      characterRefObject.isDragging = false;
      lottieAnimation.alpha = 1;
      dragOffset = { x: 0, y: 0 };

      // Stop recording if we were recording
      if (isRecordingRef.current && recordingStartTimeRef.current !== null) {
        const pathPoints = stopRecording(charactersRef, onUpdateCharacter);
        onUpdateCharacter(charData.id, { pathPoints: pathPoints });
      }
    };

    // Wheel scaling handler
    const onWheel = (event: FederatedWheelEvent) => {
      event.stopPropagation();
      const scaleDelta = event.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(
        Math.max(characterRefObject.scale * scaleDelta, 0.1),
        5
      );

      //characterRef.scale = newScale;
      lottieAnimation.scale.set(newScale);
      characterRefObject.scale = newScale;
      //updateCharacterVisuals(characterRef);
      onUpdateCharacter(charData.id, { scale: newScale });
    };

    /*

    const stopEditing = () => {
      console.log("Inside stop editing");
      if(characterRefObject && characterRefObject.borderGraphics)
        characterRefObject.borderGraphics.visible = false
    };
*/

    // Add event listeners
    lottieAnimation
      .on('pointerdown', onPointerDown)
      .on('pointermove', onPointerMove)
      .on('pointerup', onPointerUp)
      .on('pointerupoutside', onPointerOutside)
      .on('pointercancel', onPointerOutside)
      .on('wheel', onWheel);

    return characterRefObject;
  } catch (error) {
    console.error('Error setting up character:', error);
    return null;
  }
};
